import { UserRole } from "@tfg/db"
import { Loader } from "@tfg/ui"
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import { LibraryResponses } from "@tfg/types/endpoints/library"

import { httpClient } from "~/backend/axios"

type AuthContext = {
  isAuthenticated: boolean
  isLoading: boolean
  user: LibraryResponses.TFGUser | null
  isAdmin: boolean
  logout: () => void
  authenticate: (user: LibraryResponses.TFGUser) => void
  checkAuth: () => void
}

export const AuthContext = createContext<AuthContext>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  isAdmin: false,
  logout: async () => void {},
  authenticate: () => void {},
  checkAuth: () => void {},
})

type Props = PropsWithChildren<object>

export function AuthContextProvider({ children }: Props) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [user, setUser] = useState<LibraryResponses.TFGUser | null>(null)

  const logout = useCallback(async () => {
    await httpClient.post("/auth/logout")
    setIsAuthenticated(false)
    setUser(null)
    setIsLoading(false)
  }, [])

  const isAdmin = user?.role === UserRole.ADMIN

  const authenticate = useCallback((user: LibraryResponses.TFGUser) => {
    setUser(user)
    setIsAuthenticated(true)
    setIsLoading(false)
  }, [])

  const checkAuth = useCallback(() => {
    setIsLoading(true)
    httpClient
      .post("/auth/refresh-token")
      .then((response) => {
        const user: LibraryResponses.TFGUser = response.data
        if (user) {
          setUser(user)
          setIsAuthenticated(true)
        } else {
          setIsAuthenticated(false)
        }
        setIsLoading(false)
      })
      .catch(() => {
        setIsAuthenticated(false)
        setIsLoading(false)
      })
  }, [])

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  const value: AuthContext = {
    isAuthenticated,
    isLoading,
    user,
    isAdmin,
    logout,
    authenticate,
    checkAuth,
  }

  if (isLoading) {
    return <Loader fullPage />
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within a AuthContextProvider")
  }
  return context
}
